import { analyticsPresetRange, normalizeAnalyticsRange } from '@/lib/analyticsDateRange';
import { DateRangePickerPresetKey, type DateRangePickerValue } from '@shigo-ui/ui';

export interface PerformanceAnalyzerRouteSearch {
  from?: string;
  to?: string;
  preset?: DateRangePickerPresetKey;
}

export function performanceAnalyzerRouteSearchSchema(
  search: Record<string, unknown>,
): PerformanceAnalyzerRouteSearch {
  const preset = readPreset(search['preset']);
  return {
    ...(typeof search['from'] === 'string' ? { from: search['from'] } : {}),
    ...(typeof search['to'] === 'string' ? { to: search['to'] } : {}),
    ...(preset ? { preset } : {}),
  };
}

export function parsePerformanceAnalyzerRouteSearch(
  search: PerformanceAnalyzerRouteSearch,
  now = new Date(),
): DateRangePickerValue {
  if (isIsoDate(search.from) && isIsoDate(search.to) && search.from <= search.to) {
    return normalizeAnalyticsRange(
      {
        dateFrom: search.from,
        dateTo: search.to,
        ...(search.preset ? { preset: search.preset } : {}),
      },
      now,
    );
  }
  if (search.preset) {
    return analyticsPresetRange(search.preset, now);
  }
  return defaultPerformanceAnalyzerRange(now);
}

export function serializePerformanceAnalyzerRouteSearch(range: {
  dateFrom: string;
  dateTo: string;
  preset?: string | undefined;
}): PerformanceAnalyzerRouteSearch {
  const preset = readPreset(range.preset);
  return {
    from: range.dateFrom,
    to: range.dateTo,
    ...(preset ? { preset } : {}),
  };
}

export function defaultPerformanceAnalyzerRange(now = new Date()): DateRangePickerValue {
  return analyticsPresetRange(DateRangePickerPresetKey.Last7Days, now);
}

function readPreset(value: unknown) {
  return Object.values(DateRangePickerPresetKey).find((preset) => preset === value);
}

function isIsoDate(value: string | undefined): value is string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return false;
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
}
